/**
 * Cross-tenant marketing leads — super-admin only.
 *
 * Surfaces inbound demo / sales requests from hooddesk.com. Platform-level
 * table (no society_id, no RLS); the GET endpoint is super-admin gated and
 * uses `withoutTenant` server-side.
 *
 * Layout: PageHeader → 5-status KPI strip → filter row → table. Kebab menu
 * per row drives status transitions, notes, and quick contact (WhatsApp +
 * tel: deep-links).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Megaphone,
  Search,
  Filter,
  Phone,
  MessageCircle,
  MoreVertical,
  Pencil,
  Copy,
  Check,
  X,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Clock3,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatRelative } from '@/lib/format';
import { downloadAsCsv, csvFilename } from '@/lib/download';

type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost'
};

const STATUS_VARIANTS: Record<LeadStatus, 'info' | 'warn' | 'ok' | 'alert' | 'muted'> = {
  new: 'info',
  contacted: 'warn',
  qualified: 'ok',
  converted: 'ok',
  lost: 'muted'
};

interface MarketingLead {
  id: string;
  full_name: string;
  phone: string;
  email: string | null;
  society_name: string | null;
  units_count: number | null;
  city: string | null;
  source: string | null;
  message: string | null;
  status: LeadStatus;
  contacted_at: string | null;
  notes: string | null;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  items: MarketingLead[];
  next_cursor: string | null;
  kpi: Record<LeadStatus, number>;
}

const ALL_STATUSES: LeadStatus[] = ['new', 'contacted', 'qualified', 'converted', 'lost'];

function buildWhatsAppLink(phone: string, name: string): string {
  // Drop the '+' so wa.me accepts the number; keep it for tel: links.
  const digits = phone.replace(/[^0-9]/g, '');
  const greeting = `Hi ${name.split(' ')[0] || 'there'}, this is HoodDesk — thanks for requesting a demo for your society. When is a good time to call?`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(greeting)}`;
}

export function SuperAdminMarketingLeadsPage(): JSX.Element {
  const queryClient = useQueryClient();

  // ─── Filter state ─────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>(''); // debounced
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Debounce free-text search — 300ms idle.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ─── Edit dialog state ────────────────────────────────────
  const [editLead, setEditLead] = useState<MarketingLead | null>(null);
  const [editStatus, setEditStatus] = useState<LeadStatus>('new');
  const [editNotes, setEditNotes] = useState<string>('');

  useEffect(() => {
    if (editLead) {
      setEditStatus(editLead.status);
      setEditNotes(editLead.notes ?? '');
    }
  }, [editLead]);

  // ─── Query ────────────────────────────────────────────────
  const leadsFiltersHash = useMemo(
    () => JSON.stringify([statusFilter, search]),
    [statusFilter, search]
  );
  const [leadsCursor, setLeadsCursor] = useState<string | null>(null);
  useEffect(() => {
    setLeadsCursor(null);
  }, [leadsFiltersHash]);
  const list = useQuery({
    queryKey: ['superadmin', 'marketing-leads', { statusFilter, search, leadsCursor }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('q', search);
      if (leadsCursor) params.set('cursor', leadsCursor);
      return get<ListResponse>(`/marketing/leads?${params.toString()}`);
    },
    refetchOnWindowFocus: false
  });
  const leadsPaginator = useCursorAccumulator<MarketingLead>(list.data, leadsCursor, leadsFiltersHash);

  // ─── Mutation: patch lead ─────────────────────────────────
  const patchLead = useMutation({
    mutationFn: (payload: { id: string; body: Record<string, unknown> }) =>
      patch<MarketingLead>(`/superadmin/marketing-leads/${payload.id}`, payload.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'marketing-leads'] });
      toast.success('Lead updated');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const handleSaveEdit = (): void => {
    if (!editLead) return;
    const body: Record<string, unknown> = {};
    if (editStatus !== editLead.status) body.status = editStatus;
    if ((editNotes || null) !== (editLead.notes ?? null)) {
      body.notes = editNotes.trim() ? editNotes.trim() : null;
    }
    if (Object.keys(body).length === 0) {
      toast.message('Nothing changed');
      setEditLead(null);
      return;
    }
    patchLead.mutate(
      { id: editLead.id, body },
      { onSuccess: () => setEditLead(null) }
    );
  };

  const quickStatus = (id: string, status: LeadStatus): void => {
    patchLead.mutate({ id, body: { status } });
  };

  const copyToClipboard = async (id: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      toast.success('Phone copied');
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      toast.error('Could not access clipboard');
    }
  };

  const kpi = list.data?.kpi ?? { new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 };
  const items = leadsPaginator.items;

  const isFiltered = Boolean(statusFilter || search);

  // ─── CSV export ──────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async (): Promise<void> => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (statusFilter) p.set('status', statusFilter);
      if (search) p.set('q', search);
      const url = `/superadmin/marketing-leads/export.csv${p.toString() ? `?${p.toString()}` : ''}`;
      const result = await downloadAsCsv(url, csvFilename('leads'));
      toast.success(`Downloaded ${result.rowCount} rows`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Marketing leads"
        description="Demo / sales requests submitted from hooddesk.com — across every tenant."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExportCsv()}
            loading={exporting}
            loadingText="Exporting…"
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-vt-3 sm:grid-cols-4">
          <KpiCard
            label="New"
            value={kpi.new}
            icon={<Sparkles className="h-5 w-5" strokeWidth={1.75} />}
            tone="info"
            isLoading={list.isLoading}
            active={statusFilter === 'new'}
            onClick={() => setStatusFilter(statusFilter === 'new' ? '' : 'new')}
          />
          <KpiCard
            label="Contacted"
            value={kpi.contacted}
            icon={<Clock3 className="h-5 w-5" strokeWidth={1.75} />}
            tone="warn"
            isLoading={list.isLoading}
            active={statusFilter === 'contacted'}
            onClick={() => setStatusFilter(statusFilter === 'contacted' ? '' : 'contacted')}
          />
          <KpiCard
            label="Qualified"
            value={kpi.qualified}
            icon={<CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />}
            tone="ok"
            isLoading={list.isLoading}
            active={statusFilter === 'qualified'}
            onClick={() => setStatusFilter(statusFilter === 'qualified' ? '' : 'qualified')}
          />
          <KpiCard
            label="Converted"
            value={kpi.converted}
            icon={<Megaphone className="h-5 w-5" strokeWidth={1.75} />}
            tone="ok"
            isLoading={list.isLoading}
            active={statusFilter === 'converted'}
            onClick={() => setStatusFilter(statusFilter === 'converted' ? '' : 'converted')}
          />
        </div>

        {/* Filter row */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
            <div className="flex items-center gap-vt-2 self-center">
              <Filter className="h-4 w-4 text-vt-text-muted" />
              <span className="text-vt-12 text-vt-text-muted">Filters:</span>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="leads-status">Status</Label>
              <Select
                id="leads-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter((e.target.value || '') as LeadStatus | '')}
                style={{ minWidth: 160 }}
              >
                <option value="">All statuses</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-1 flex-col gap-1" style={{ minWidth: 240 }}>
              <Label htmlFor="leads-search">Search</Label>
              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-muted"
                />
                <Input
                  id="leads-search"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, phone, society, email, city…"
                  className="pl-9"
                />
              </div>
            </div>

            {isFiltered ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('');
                  setSearchInput('');
                  setSearch('');
                }}
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            ) : null}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {list.isLoading ? (
              <div className="p-vt-6 space-y-vt-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <div className="p-vt-6">
                <EmptyState
                  icon={<AlertCircle className="h-6 w-6" />}
                  title="Could not load leads"
                  description={getErrorMessage(list.error)}
                  action={
                    <Button variant="outline" onClick={() => list.refetch()}>
                      Retry
                    </Button>
                  }
                />
              </div>
            ) : items.length === 0 ? (
              <div className="p-vt-6">
                <EmptyState
                  icon={<Megaphone className="h-6 w-6" />}
                  title={isFiltered ? 'No leads match your filters' : 'No marketing leads yet'}
                  description={
                    isFiltered
                      ? 'Try widening the filters or clearing the search box.'
                      : 'When prospects submit the demo form on hooddesk.com, they will appear here.'
                  }
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Society</TableHead>
                      <TableHead className="text-right">Units</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((lead) => (
                      <LeadRow
                        key={lead.id}
                        lead={lead}
                        copiedId={copiedId}
                        onCopyPhone={(v) => copyToClipboard(lead.id, v)}
                        onEdit={() => setEditLead(lead)}
                        onQuickStatus={(s) => quickStatus(lead.id, s)}
                      />
                    ))}
                  </TableBody>
                </Table>
                <CursorPaginator
                  hasMore={Boolean(leadsPaginator.nextCursor)}
                  loading={list.isFetching}
                  itemsShown={items.length}
                  pageSize={50}
                  onLoadMore={() => {
                    if (leadsPaginator.nextCursor) setLeadsCursor(leadsPaginator.nextCursor);
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editLead} onOpenChange={(open) => !open && setEditLead(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update lead</DialogTitle>
            <DialogDescription>
              {editLead ? (
                <span>
                  {editLead.full_name} — {editLead.phone}
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {editLead ? (
            <div className="space-y-vt-4">
              {editLead.message ? (
                <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text-muted">
                  <div className="mb-1 text-vt-12 font-semibold text-vt-text">Their message</div>
                  {editLead.message}
                </div>
              ) : null}

              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  id="edit-status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as LeadStatus)}
                  className="mt-1"
                >
                  {ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-notes">Internal notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder="What happened on the call? Next step?"
                  className="mt-1"
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLead(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={patchLead.isPending}>
              {patchLead.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: 'info' | 'warn' | 'ok' | 'alert';
  isLoading: boolean;
  active: boolean;
  onClick: () => void;
}

function KpiCard({ label, value, icon, tone, isLoading, active, onClick }: KpiCardProps): JSX.Element {
  const toneClass: Record<KpiCardProps['tone'], string> = {
    info: 'bg-vt-info-100 text-vt-info',
    warn: 'bg-[color:var(--vt-warn-100)] text-vt-warn',
    ok: 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
    alert: 'bg-vt-alert-100 text-vt-alert'
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-vt-md border bg-vt-surface text-left transition-shadow ' +
        (active ? 'border-vt-primary shadow-vt-card' : 'border-vt-border hover:shadow-vt-card')
      }
      aria-pressed={active}
    >
      <div className="flex items-center gap-vt-3 p-vt-4">
        <span className={'flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ' + toneClass[tone]}>
          {icon}
        </span>
        <div className="min-w-0">
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-24 font-semibold text-vt-text">
            {isLoading ? <Skeleton className="h-7 w-12" /> : value}
          </div>
        </div>
      </div>
    </button>
  );
}

interface LeadRowProps {
  lead: MarketingLead;
  copiedId: string | null;
  onCopyPhone: (value: string) => void;
  onEdit: () => void;
  onQuickStatus: (s: LeadStatus) => void;
}

function LeadRow({ lead, copiedId, onCopyPhone, onEdit, onQuickStatus }: LeadRowProps): JSX.Element {
  const variant = STATUS_VARIANTS[lead.status];
  const waLink = useMemo(() => buildWhatsAppLink(lead.phone, lead.full_name), [lead.phone, lead.full_name]);

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-vt-text">{lead.full_name}</div>
        {lead.email ? <div className="text-vt-12 text-vt-text-muted">{lead.email}</div> : null}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-vt-2">
          <a
            href={`tel:${lead.phone}`}
            className="font-mono text-vt-13 text-vt-text hover:text-vt-primary hover:underline"
          >
            {lead.phone}
          </a>
          <button
            type="button"
            onClick={() => onCopyPhone(lead.phone)}
            aria-label="Copy phone number"
            title="Copy phone number"
            className="inline-flex h-6 w-6 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors hover:bg-vt-surface-2 hover:text-vt-text"
          >
            {copiedId === lead.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open WhatsApp"
            title="Open WhatsApp"
            className="inline-flex h-6 w-6 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors hover:bg-vt-surface-2 hover:text-vt-text"
          >
            <MessageCircle className="h-3.5 w-3.5" />
          </a>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-vt-13 text-vt-text">{lead.society_name ?? '—'}</span>
      </TableCell>
      <TableCell className="text-right">
        <span className="text-vt-13 text-vt-text">{lead.units_count ?? '—'}</span>
      </TableCell>
      <TableCell>
        <span className="text-vt-13 text-vt-text">{lead.city ?? '—'}</span>
      </TableCell>
      <TableCell>
        {lead.source ? (
          <span className="rounded-vt-sm bg-vt-surface-2 px-2 py-0.5 font-mono text-vt-12 text-vt-text-muted">
            {lead.source}
          </span>
        ) : (
          <span className="text-vt-text-muted">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={variant}>{STATUS_LABELS[lead.status]}</Badge>
      </TableCell>
      <TableCell>
        <span className="text-vt-12 text-vt-text-muted" title={lead.created_at}>
          {formatRelative(lead.created_at)}
        </span>
      </TableCell>
      <TableCell>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Open actions menu"
              className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors hover:bg-vt-surface-2 hover:text-vt-text"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="z-50 min-w-[200px] rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-card"
            >
              <DropdownMenu.Item
                onSelect={onEdit}
                className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
              >
                <Pencil className="h-4 w-4" />
                Edit lead
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />

              <DropdownMenu.Label className="px-vt-3 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-vt-text-soft">
                Quick status
              </DropdownMenu.Label>
              {ALL_STATUSES.filter((s) => s !== lead.status).map((s) => (
                <DropdownMenu.Item
                  key={s}
                  onSelect={() => onQuickStatus(s)}
                  className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                >
                  Mark as {STATUS_LABELS[s].toLowerCase()}
                </DropdownMenu.Item>
              ))}

              <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />

              <DropdownMenu.Item
                asChild
                className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
              >
                <a href={`tel:${lead.phone}`}>
                  <Phone className="h-4 w-4" />
                  Call
                </a>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                asChild
                className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
              >
                <a href={waLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </TableCell>
    </TableRow>
  );
}
